import PaginatedList, {
  collectGridCardIdentities,
  registerGridCardIdentity
} from '@theme/paginated-list'
import {
  resetCollectionGroupsAggregation,
  runCollectionGroupsAggregation
} from '@theme/collection-groups-aggregation'

/**
 * A custom element that renders a paginated results list
 */
export default class ResultsList extends PaginatedList {
  async preparePaginatedList() {
    await runCollectionGroupsAggregation(this)
  }

  shouldObserveInfiniteScroll() {
    const grid =
      this.refs?.grid ||
      this.querySelector(
        '.product-groups-wrapper[ref="grid"], .product-groups-wrapper'
      )

    if (
      grid instanceof HTMLElement &&
      grid.dataset.collectionGrouped === 'true' &&
      grid.dataset.collectionGroupsLoaded === 'true' &&
      grid.dataset.collectionGroupsFailed !== 'true'
    ) {
      return false
    }

    return super.shouldObserveInfiniteScroll()
  }

  onPaginatedFilterUpdate() {
    resetCollectionGroupsAggregation(this)
  }

  connectedCallback() {
    super.connectedCallback()

    this.setAttribute('initialized', '')
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    resetCollectionGroupsAggregation(this)
  }

  /**
   * Overridden to handle grouping logic.
   *
   * @param {NodeListOf<Element> | Element[]} elements
   * @param {"previous" | "next"} type
   */
  insertNewPageElements(elements, type) {
    const { grid } = this.refs
    if (!grid) return

    // Standard mode fallback
    if (!grid.classList.contains('product-groups-wrapper')) {
      super.insertNewPageElements(elements, type)
      return
    }

    const cards = Array.from(elements)
    const existingIdentities = collectGridCardIdentities(grid)

    // For 'previous', we need to iterate in reverse to maintain order when prepending
    if (type === 'previous') {
      cards.reverse()
    }

    for (const cardItem of cards) {
      if (!(cardItem instanceof HTMLElement)) continue
      const card = /** @type {HTMLElement} */ (cardItem)

      const groupId = card.dataset.groupId
      const groupTitle = card.dataset.groupTitle

      if (!groupId) continue
      if (registerGridCardIdentity(card, existingIdentities)) continue

      let groupContainer = grid.querySelector(
        `.product-group[data-group-id="${groupId}"]`
      )

      if (!groupContainer) {
        const sectionId = this.getAttribute('section-id')
        groupContainer = document.createElement('details')
        groupContainer.open = true
        groupContainer.classList.add('product-group')
        groupContainer.dataset.groupId = groupId
        if (sectionId) {
          groupContainer.id = `ProductGroup-${sectionId}-${groupId}`
        }

        if (groupTitle) {
          const titleSummary = document.createElement('summary')
          titleSummary.classList.add('product-group__title')

          const label = document.createElement('span')
          label.classList.add('product-group__label')
          label.textContent = groupTitle
          titleSummary.append(label)

          const toggle = document.createElement('span')
          toggle.classList.add('product-group__toggle')
          toggle.setAttribute('aria-hidden', 'true')

          const chevronSvg = document.createElementNS(
            'http://www.w3.org/2000/svg',
            'svg'
          )
          chevronSvg.setAttribute('aria-hidden', 'true')
          chevronSvg.setAttribute('focusable', 'false')
          chevronSvg.classList.add('icon', 'icon-caret')
          chevronSvg.setAttribute('viewBox', '0 0 10 6')

          const path = document.createElementNS(
            'http://www.w3.org/2000/svg',
            'path'
          )
          path.setAttribute('fill-rule', 'evenodd')
          path.setAttribute('clip-rule', 'evenodd')
          path.setAttribute(
            'd',
            'M9.354.646a.5.5 0 00-.708 0L5 4.293 1.354.646a.5.5 0 00-.708.708l4 4a.5.5 0 00.708 0l4-4a.5.5 0 000-.708z'
          )
          path.setAttribute('fill', 'currentColor')

          chevronSvg.append(path)
          toggle.append(chevronSvg)
          titleSummary.append(toggle)
          groupContainer.append(titleSummary)
        }

        const listUl = document.createElement('ul')
        // Try to copy classes/attributes from an existing grid if available
        const existingUl = grid.querySelector('ul.product-grid')
        if (existingUl instanceof HTMLElement) {
          listUl.className = existingUl.className
          if (existingUl.hasAttribute('product-grid-view')) {
            listUl.setAttribute(
              'product-grid-view',
              existingUl.getAttribute('product-grid-view') || 'default'
            )
          }
          listUl.style.cssText = existingUl.style.cssText
        } else {
          // Fallback based on typical liquid rendering
          listUl.classList.add(
            'product-grid',
            `product-grid--${sectionId}`,
            'product-grid--grid'
          )
          listUl.style.setProperty('--mobile-columns', '2')
        }
        listUl.setAttribute('role', 'list')

        groupContainer.appendChild(listUl)

        // Insert group into main wrapper
        if (groupId === 'others') {
          grid.appendChild(groupContainer)
        } else if (type === 'next') {
          // If defining a new group while scrolling down
          // If "others" exists and this is not it, insert before "others"
          const othersGroup = grid.querySelector(
            '.product-group[data-group-id="others"]'
          )
          if (othersGroup) {
            grid.insertBefore(groupContainer, othersGroup)
          } else {
            grid.appendChild(groupContainer)
          }
        } else {
          // For previous, we usually want to prepend new groups to the top
          grid.prepend(groupContainer)
        }
      }

      const list = groupContainer.querySelector('ul')
      if (list) {
        groupContainer.hidden = false
        groupContainer.removeAttribute('data-group-empty')

        if (type === 'next') {
          list.appendChild(card)
        } else {
          list.prepend(card)
        }
      }
    }
  }
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList)
}
